"use client";
import GridIcon from "@/app/assets/images/svg/grid.svg";
import ListIcon from "@/app/assets/images/svg/list.svg";
import ProductCard from "@/components/ProductCard/ProductCard";
import IconOnlyButton from "@/components/UI/Button/IconOnlyButton/IconOnlyButton";
import { EnrichedSearchRequestModel } from "@/lib/searchspring/types";
import { SearchResponseModelMerchandisingContentInline } from "@searchspring/snapi-types";
import clsx from "clsx";
import ProductListItem from "../ProductListItem/ProductListItem";
import { SearchSpringInlineBanner } from "../SearchSpringMerchandising/SearchSpringMerchandising";
import SortAndFilter from "../SortAndFilter/SortAndFilter";
import styles from "./ProductGrid.module.scss";
import { useContext, useState } from "react";
import { Category, ProductData } from "@/lib/5874/types";
import { BigCommerceStoreContext } from "@/providers/BigCommerceStoreProvider";

export type ViewStyle = "grid" | "list";

interface ViewProps {
	results: ProductData[] | undefined;
	category?: Category;
	currencyCode: string;
}

interface GridViewProps extends ViewProps {
	banners: Array<SearchResponseModelMerchandisingContentInline> | undefined;
}

type ToggleProps = {
	results: EnrichedSearchRequestModel;
	totalResults: number;
	suggestedCategories: any[] | undefined;
	itemsFrom: number;
	itemsTo: number;
	category?: Category;
};

export const ToggleView = ({
	results,
	totalResults,
	suggestedCategories,
	itemsFrom,
	itemsTo,
	category,
}: ToggleProps) => {
	const [view, setView] = useState<ViewStyle>("grid");
	const { currencyFormattingRules } = useContext(BigCommerceStoreContext);
	const handleChangeView = (selectedView: ViewStyle) => {
		setView(selectedView);
	};

	const currencyCode = currencyFormattingRules.currency;

	return (
		<>
			<div className={styles.filters}>
				<SortAndFilter
					filters={results.facets}
					suggestedCategories={suggestedCategories}
				/>
				{totalResults > 0 && (
					<span className={styles.count}>
						{itemsFrom}-{itemsTo} of {totalResults}{" "}
						{totalResults > 1 ? "items" : "item"}
					</span>
				)}
				<div className={styles.view}>
					<IconOnlyButton
						variant='secondary'
						onClick={() => handleChangeView("grid")}
						className={clsx([
							styles.iconOnlyButton,
							view === "grid" ? styles.active : null,
						])}
						icon={<GridIcon />}
					/>
					<IconOnlyButton
						variant='secondary'
						onClick={() => handleChangeView("list")}
						className={clsx([
							styles.iconOnlyButton,
							view === "list" ? styles.active : null,
						])}
						icon={<ListIcon />}
					/>
				</div>
			</div>
			{view === "grid" ? (
				<GridView
					banners={results.merchandising?.content?.inline}
					results={results.results}
					category={category}
					currencyCode={currencyCode}
				/>
			) : (
				<ListView
					results={results.results}
					category={category}
					currencyCode={currencyCode}
				/>
			)}
		</>
	);
};

const GridView = ({
	banners = [],
	results = [],
	category,
	currencyCode,
}: GridViewProps) => {
	return (
		<div className={styles.gridView}>
			{results?.map((product, index) => {
				const banner = banners.find(
					(banner) => banner.config?.position?.index === index,
				);

				return (
					<div key={product.id}>
						<SearchSpringInlineBanner {...banner} />
						<ProductCard
							product={product}
							category={category}
							currencyCode={currencyCode}
							isInRecommendationCarousel={false}
						/>
					</div>
				);
			})}
		</div>
	);
};

const ListView = ({ results = [], category, currencyCode }: ViewProps) => {
	return (
		<div className={styles.listView}>
			{results?.map((product) => {
				return (
					<ProductListItem
						key={product.id}
						product={product}
						category={category}
						currencyCode={currencyCode}
					/>
				);
			})}
		</div>
	);
};
